/**
 * Global shortcuts for the Ask panel: toggle it, and start a new chat from anywhere.
 *
 * Bound on the window, same pattern as `Sidebar`'s `sidebarToggle` listener — mounted
 * once in `Layout`, guarded by `isTypingTarget` so neither steals a keystroke from
 * someone typing in a field or the composer itself.
 *
 * Dictation's binding is deliberately NOT handled here. It is the one shortcut in the
 * catalog that must fire WHILE the composer has focus (that is the surface it drives),
 * so it is matched locally inside `AskComposer` instead, without the `isTypingTarget`
 * guard this hook relies on for the other two.
 */
import { useEffect } from 'react'

import { useAskStore } from 'ask'
import { eventMatchesBinding, isTypingTarget } from 'utils'

import { useShortcutBindings } from './useShortcutBindings'

export const useAskShortcuts = () => {
  // Live bindings, not a one-off read — a rebind in Settings must take effect for a
  // listener that is already mounted, the same requirement `Sidebar` has for ⌘B.
  const { askToggle, askNewChat } = useShortcutBindings()

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return

      if (eventMatchesBinding(event, askToggle)) {
        event.preventDefault()
        useAskStore.getState().togglePanel()
        return
      }

      if (eventMatchesBinding(event, askNewChat)) {
        event.preventDefault()
        const store = useAskStore.getState()
        // Opening first, not just starting a conversation that nothing is showing —
        // the shortcut should work from anywhere, not only while the panel is
        // already on screen.
        store.openPanel()
        store.startConversation()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [askToggle, askNewChat])
}
